import { Injectable, signal } from '@angular/core';
import { ProjectProgressChartModel } from '../../../../../core/models/project/project-progress-chart.model';
import { DateTimeHelper } from '../../../../../features/calendar/util/date-time-helper';
import { DropdownOption } from '../../../../../shared/data-types/dropdown-option';
import { ApplicableChartTimeUnits } from '../../../shared/project-detail-types';
import { TimeUnits } from '../../../../../shared/data-types/unit-types';

@Injectable({
  providedIn: 'root',
})
export class ProjectProgressChartService {
  public chartModel = signal(new ProjectProgressChartModel());

  constructor() {
    const today = new Date();
    this.updateMonthViewDates(today, TimeUnits.Month);
  }

  public switchChartView(option: DropdownOption): void {
    const timeUnit = +option.value;
    const chartModel = this.chartModel();

    if (timeUnit === TimeUnits.Month) {
      this.updateMonthViewDates(chartModel.startDate, timeUnit);
    }

    if (timeUnit === TimeUnits.Year) {
      this.updateYearViewDates(chartModel.startDate, timeUnit);
    }
  }

  public prev(): void {
    const monthFn = DateTimeHelper.subMonths;
    const yearFn = DateTimeHelper.subYears;
    this.navigate(monthFn, yearFn, TimeUnits.Month);
  }

  public next(): void {
    const monthFn = DateTimeHelper.addMonths;
    const yearFn = DateTimeHelper.addYears;
    this.navigate(monthFn, yearFn, TimeUnits.Month);
  }

  public today(): void {
    const viewMode = this.chartModel().viewMode;
    const today = new Date();
    if (viewMode === TimeUnits.Month) {
      this.updateMonthViewDates(today, TimeUnits.Month);
    }

    if (viewMode === TimeUnits.Year) {
      this.updateYearViewDates(today, TimeUnits.Year);
    }
  }

  private navigate(
    monthFn: (d, n) => Date,
    yearFn: (d, n) => Date,
    timeUnit: ApplicableChartTimeUnits,
  ): void {
    if (timeUnit === TimeUnits.Month) {
      this.chartModel.update((model) => {
        return {
          ...model,
          startDate: DateTimeHelper.startOfMonth(monthFn(model.startDate, 1)),
          endDate: DateTimeHelper.endOfMonth(monthFn(model.endDate, 1)),
        };
      });
    }

    if (timeUnit === TimeUnits.Year) {
      this.chartModel.update((model) => {
        return {
          ...model,
          startDate: DateTimeHelper.startOfYear(yearFn(model.startDate, 1)),
          endDate: DateTimeHelper.endOfYear(yearFn(model.endDate, 1)),
        };
      });
    }
  }

  private updateMonthViewDates(date: Date, timeUnit: ApplicableChartTimeUnits): void {
    this.chartModel.update((model) => {
      return {
        ...model,
        startDate: DateTimeHelper.startOfMonth(date),
        endDate: DateTimeHelper.endOfMonth(date),
        viewMode: timeUnit,
      };
    });
  }

  private updateYearViewDates(date: Date, timeUnit: ApplicableChartTimeUnits): void {
    this.chartModel.update((model) => {
      return {
        ...model,
        startDate: DateTimeHelper.startOfYear(date),
        endDate: DateTimeHelper.endOfYear(date),
        viewMode: timeUnit,
      };
    });
  }
}
